import type {
  PartSearchHit,
  PartSearchResponse,
  SearchPartsParams,
  PartSearchHttpClient,
} from "../types";

function pickNonNegativeNumber(v: unknown): number {
  if (v == null || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function mapTypesenseHighlightResult(
  hit: Record<string, unknown>,
): PartSearchHit["_highlightResult"] | undefined {
  const adapted = hit._highlightResult as
    | PartSearchHit["_highlightResult"]
    | undefined;
  if (adapted?.partNumber?.value || adapted?.partName?.value) {
    return adapted;
  }

  const highlight = hit.highlight as
    | Record<string, { value?: string; snippet?: string }>
    | undefined;
  if (highlight) {
    const result: NonNullable<PartSearchHit["_highlightResult"]> = {};
    const partNumberValue = highlight.partNumber?.value ?? highlight.partNumber?.snippet;
    const partNameValue = highlight.partName?.value ?? highlight.partName?.snippet;
    if (partNumberValue) result.partNumber = { value: partNumberValue };
    if (partNameValue) result.partName = { value: partNameValue };
    if (Object.keys(result).length) return result;
  }

  const highlights = hit.highlights as
    | Array<{ field?: string; snippet?: string; value?: string }>
    | undefined;
  if (highlights?.length) {
    const result: NonNullable<PartSearchHit["_highlightResult"]> = {};
    for (const item of highlights) {
      const field = item.field;
      const value = item.value ?? item.snippet;
      if (!field || !value) continue;
      if (field === "partNumber" || field === "partName") {
        result[field] = { value };
      }
    }
    if (Object.keys(result).length) return result;
  }

  return undefined;
}

/** Map Typesense hit / document to a normalized part search row. */
export function mapPartSearchHit(hit: unknown): PartSearchHit | null {
  const h = hit as Record<string, unknown>;
  const doc = (h.document ?? h) as Record<string, unknown>;
  const rawId = doc.id ?? doc._id ?? h._id;
  const id =
    typeof rawId === "string"
      ? rawId
      : rawId && typeof rawId === "object" && "$oid" in rawId
        ? String((rawId as { $oid: string }).$oid)
        : rawId != null
          ? String(rawId)
          : "";
  if (!id) return null;
  const highlightResult = mapTypesenseHighlightResult(h);
  return {
    _id: id,
    partNumber: (doc.partNumber ?? doc["Part Number"]) as string | undefined,
    partName: (doc.partName ?? doc["Part Name"] ?? doc.name) as string | undefined,
    brandName: (doc.brandName ?? doc.brand) as string | undefined,
    mrp: pickNonNegativeNumber(
      doc.mrp ?? doc.MRP ?? doc.maxRetailPrice ?? doc.max_retail_price,
    ),
    brandId: (doc.brandId ?? doc.brand_id) as string | undefined,
    hsnCode: (doc.hsnCode ?? doc.hsn_code ?? doc["HSN code"]) as string | undefined,
    rootPartNumber: (doc.rootPartNumber ??
      doc.root_part_number ??
      doc["Root Part Number"]) as string | undefined,
    ...(highlightResult ? { _highlightResult: highlightResult } : {}),
  };
}

export const DEFAULT_PART_SEARCH_URL = "/part-master/typesense-part-search";

export function createSearchParts(
  http: PartSearchHttpClient,
  searchUrl: string = DEFAULT_PART_SEARCH_URL,
) {
  return async function searchParts(params: SearchPartsParams): Promise<PartSearchResponse> {
    const { data } = await http.get<{ items: unknown[]; page: number; hasMore: boolean }>(
      searchUrl,
      {
        params: {
          q: params.q,
          page: params.page,
          limit: params.limit,
          ...(params.brand?.trim() ? { brand: params.brand.trim() } : {}),
        },
      },
    );
    return {
      page: data.page,
      hasMore: data.hasMore,
      items: (data.items ?? []).map(mapPartSearchHit).filter((x): x is PartSearchHit => x != null),
    };
  };
}

/** @deprecated Use `createSearchParts(axios)` instead. */
export type SearchPartsFn = (params: SearchPartsParams) => Promise<PartSearchResponse>;
